import {
  type AllListingsSortByEnum,
  BuyingFormatEnum,
} from '@b-stock/search-api-client'

import listingSearchQuery from './listingSearchQuery'
import type { SearchParams } from './listingSearchQuery'

// Fewer than 3 unique bidders reads as "low competition" per the module's
// product intent (GLOB-5150).
const MAX_UNIQUE_BIDDERS = 2

// Loose bid-count ceiling so listings inflated by repeat bidding from a
// single buyer (uniqueBidders low, numberOfBids higher) still make it into
// the fetched pool for the precise client-side filter below. There is no
// maxUniqueBidders search param (see GLOB-5150 / BP-2944 through BP-2947,
// closed), so this is a deliberately loose pre-filter, not the real
// criterion — a listing with more total bids than this from very few
// unique bidders is a rare edge case this won't catch.
const MAX_BID_QUANTITY_CEILING = 10

const CANDIDATE_POOL_SIZE = 50
const WINDOW_MS = 4 * 60 * 60 * 1000 // 4 hours
const HOUR_MS = 60 * 60 * 1000

/**
 * Rounds the closing-window end up to the next UTC hour boundary so the
 * query key stays stable across renders and matches server-side prefetching
 * (see betterOddsCandidatesQuery's getMaxEndTime for the same trick) — a
 * mismatched param otherwise produces a cache miss and defeats the prefetch
 * silently.
 */
const getMaxEndTime = (now: number): string =>
  new Date(Math.ceil((now + WINDOW_MS) / HOUR_MS) * HOUR_MS).toISOString()

/**
 * Shared with server-side prefetching (PrefetchedWinOpportunitiesModule) so
 * the prefetched query key matches this hook's.
 */
export const getWinOpportunitiesSearchParams = (
  now: number = Date.now()
): SearchParams => ({
  params: {
    // Only listings open for bidding: no Buy Now Only, and no Buy Now phase.
    buyingFormat: [BuyingFormatEnum.Auction],
    maxBidQuantity: MAX_BID_QUANTITY_CEILING,
    maxEndTime: getMaxEndTime(now),
    // not yet in AllListingsSortByEnum — cast until search-api-client is
    // updated (BP-2918)
    sortBy: 'personalization' as AllListingsSortByEnum,
    limit: CANDIDATE_POOL_SIZE,
  },
  usePostEndpoint: true,
  fetchFilters: false,
})

export const useWinOpportunities = () => {
  const { data, isLoading, isError, refetch } = listingSearchQuery.useWithOpts(
    [getWinOpportunitiesSearchParams()],
    {}
  )

  // Only narrows the set — never reorders it, so the server's
  // personalization ranking (best-fit-for-this-buyer first) is preserved.
  // A missing count is unknown, not zero — enterprise listings have none —
  // so those are skipped, as Better Odds does.
  const listings = (data?.listings ?? []).filter((listing) => {
    const uniqueBidders = listing.auction3MP?.uniqueBidders
    return (
      typeof uniqueBidders === 'number' && uniqueBidders <= MAX_UNIQUE_BIDDERS
    )
  })

  return {
    listings,
    isLoading,
    isError,
    refetch,
  }
}
